<template>
    <div>
        <div :class="`app-${app.uuid}`"></div>

        <b-alert :show="errorModal.show" variant="danger">
            {{ errorModal.msg }}
        </b-alert>
    </div>
</template>

<script>

import {
    Thunder,
    Component,
    Project,
    Integrations,
    Ratchet,
    Sandbox,
    Functions,
    PRR,
} from '@protokol/platform-libs'

import * as SDK from '@ptkl/sdk'
import * as SDK10 from 'ptklsdk10'
import axios from 'axios'
import moment from 'moment'
import Vue from "vue"
import VueRouter from 'vue-router'
import Loader from '@/components/renderer/loaders/engine'
import Runner from '@/components/renderer/runner'
import { Howl } from 'howler'
import _ from 'lodash'

// adapters
import PlatformAdapter from '@/lib/adapters/platform'

// isolated app
import TemplateComponents from './templates'
import FactorI18N from './translation'
import Vuex from 'vuex'

export default {
    name: 'PowerAppV5',
    components: {},
    props: {
        app: {
            type: Object
        },
        entrypoint: {
            type: String,
            default: "index"
        },
        dist: {
            type: Object
        },
        noRouter: {
            type: Boolean,
            default: false
        },
        version : {
            type: Number,
            default: 1
        }
    },

    data: function () {
        return {
            context: {
                vars: {}
            },
            errorModal: {
                show: false,
                msg: null
            },
            modules: {},
            exports: {},
            components: {},
            services: {},
            packageImports: {},
        }
    },

    methods: {
        showErrorModal(msg) {
            this.errorModal.show = true;
            this.errorModal.msg = msg;
        },

        async register(node) {
            try {
                let result = this.runJS(node.script, this.internalEssentials())

                if (result && result.stack && result.message) {
                    throw new Error(result.message)
                }

                const { render, staticRenderFns } = node

                result.components = {
                    ...result.components || {},
                    ...this.components,
                }

                // scope styles only to the component
                this.parseStyles(node.style)

                return {
                    ...result,
                    render: new Function(render),
                    staticRenderFns: staticRenderFns.map(f => {
                        return new Function(f)
                    })
                }
            } catch (err) {
                console.error(err)
            }
        },

        async parseStyles(content) {
            // Create a new <style> element
            const style = document.createElement('style');

            // Define your CSS as a string
            const css = content
            style.class = `appstyles-${this.app.uuid}`;
            // Set the content of the <style> element
            style.textContent = css;

            // Append the <style> element to the <head> or <body>
            document.head.appendChild(style);
        },

        displayError(message) {
            this.$bvToast.toast(message, {
                variant: 'danger',
                title: "Error"
            })
        },

        loader() {
            let loader = new Loader(this.dist, {
                jsLibs: this.internalEssentials()
            })

            loader.registerLoader('vue$', (node) => {
                return () => {
                    try {
                        return {
                            component: this.register(node),
                        }
                    } catch(err) {
                        console.error(err)
                    }
                }
            })

            loader.registerFallbackLoader(".*[.js|.json|.vue]", (path) => {
                return this.libs[path]
            })

            loader.registerLoader('css$', (node) => {
                this.parseStyles(node)
            })

            return loader
        },

        internalEssentials() {
            return {
                require: (path) => this.loader().require(path),
                localStorage: undefined,
                sessionStorage: undefined,
                console: console,
                debug: {
                    add: (...args) => {
                        window.__protokol__.push(args)
                    }
                },

                getPlatformContext: () => {
                    return {
                        $router: this.$router,
                        $route: this.$route,
                    }
                },

                $sdk: {
                    version(version) {
                        switch (version) {
                            case '0.10':
                                return SDK10;
                            default:
                                return SDK;
                        }
                    }
                },
                
                exports: {}
            }
        },

        runJS(script, label) {
            try {
                return new Runner().runJS(script, this.internalEssentials(), label)
            } catch(err) {
                console.error(err)
                this.showErrorModal(err.message)
            }
        },
    },

    computed: {
        id() {
            return [...Array(length)].map(() => Math.random().toString(36)[2]).join('');
        },

        template() {
            try {
                return this.dist[this.entrypoint + ".vue"]
            } catch (err) {
                this.$debugLog(err)
                this.showErrorModal(`Did you properly formated your ${this.entrypoint} template?`);
            }
        },

        scopeId() {
            return this.app.uuid.replace(/[0-9]/g, '').replaceAll('-', '');
        },

        libs() {
            return {
                axios: axios,
                moment: moment,
                lodash: _, 

                "@protokol/sdk.js": {...SDK},
                "@protokol/thunder.js": new Thunder(new PlatformAdapter(this)),
                "@protokol/component.js": new Component(new PlatformAdapter(this)),
                "@protokol/integrations.js": new Integrations(new PlatformAdapter(this)),
                "@protokol/ratchet.js": new Ratchet(new PlatformAdapter(this)),
                "@protokol/sandbox.js": new Sandbox(new PlatformAdapter(this)),
                "@protokol/project.js": new Project(new PlatformAdapter(this)),
                "@protokol/functions.js": new Functions(new PlatformAdapter(this)),
                "@protokol/prr.js": new PRR(new PlatformAdapter(this)),

                "@protokol/log.js": {
                    info: (...args) => {
                        console.log(...args)
                    },

                    error: (err) => {
                        console.error(err)
                    }
                },

                "@protokol/util.js": {
                    dateToUserTimezone: this.$toUserTimeZone,
                    dateToTimezone: (time, zone, format) => {
                        let defaultFormat = 'YYYY-MM-DD HH:mm:ss';

                        if (format) {
                            defaultFormat = format
                        }

                        if (zone === null || zone === undefined || zone == "") {
                            return moment(time).tz(moment.tz.guess()).format(defaultFormat);
                        }

                        return moment(time).tz(zone).format(defaultFormat);
                    },

                    dateToUserTimezoneAsTime: (time) => {
                        return moment(this.$toUserTimeZone(time))
                    },

                    dateToTimezoneAsTime: (time, zone) => {
                        if (zone === null || zone === undefined || zone == "") {
                            return moment(time).tz(moment.tz.guess());
                        }

                        return moment(time).tz(zone);
                    },

                    showLoadingPopup: (t) => this.$loadingPopup(t),
                    openWindow: (link, target) => {
                        window.open(link, target)
                    },

                    interval: {
                        set: (fn, tm) => {
                            return window.setInterval(function () {
                                fn()
                            }, tm)
                        },

                        clear: (interval) => {
                            window.clearInterval(interval)
                        }
                    },

                    compileTemplate: async (template) => {
                        let compiler = new Compiler();
                        let r = await compiler.compileVue(null, template)
                        let t = await this.register(r)

                        return Vue.extend(t)
                    },

                    registerComponent: (c) => {
                        c.components = {
                            ...c.components || {},
                            ...this.components,
                            ...TemplateComponents,
                        }

                        return Vue.extend(c)
                    },

                    getPlatformLocale: () => {
                        return this.$i18n.locale
                    },

                    getCurrentProject: () => {
                        return this.$store.getters.getCurrentProject
                    },

                    translateLabel: (...args) => {
                        return this.$translateLabel(...args)
                    }
                },

                "@protokol/config.js": {
                    getEnv: (key, def) => {
                        let config = this.loader().require('config.js', { ignorePathErrors: true }) || {}
                        let currentEnv = this.$store.getters.getCurrentEnv

                        config.env.live = Object.assign(config.env.live, _.get(this.app, 'config.live', {}))
                        config.env.dev = Object.assign(config.env.dev,  _.get(this.app, 'config.dev', {}))

                        return _.get(config.env, `${currentEnv}.${key}`, def)
                    },
                    
                    currentEnv: () => {
                        return this.$store.getters.getCurrentEnv
                    },
                },

                "@protokol/eval.js": (expr, params) => {
                    let keys = Object.keys(params)
                    let values = Object.values(params)

                    let result = new Function(...keys, `try{ ${expr} } catch (err) { return err; }`)

                    return result(...values)
                },

                "@protokol/audio.js": {
                    play: (file, options) => {
                        var sound = new Howl({
                            src: [file],
                            volume: 1,
                            ...options
                        });

                        sound.play();

                        return sound
                    },

                    playNotification: (options) => {
                        var sound = new Howl({
                            src: ['../sound/notification.wav'],
                            volume: 1,
                            ...options
                        });

                        sound.play();

                        return sound
                    }
                },

                "@protokol/storage.js": {
                    get: (key) => {
                        return localStorage.getItem(`${this.app.uuid}_${key}`)
                    },

                    set: (key, value) => {
                        return localStorage.setItem(`${this.app.uuid}_${key}`, value)
                    },

                    remove: (key) => {
                        return localStorage.removeItem(`${this.app.uuid}_${key}`)
                    }
                },

                "@protokol/user.js": {
                    roles: () => {
                        return this.$store.dispatch('fetchRoles')
                    },

                    can: (action) => {
                        return this.$can(action)
                    },

                    allowed: (action) => {
                        return this.$can(`${action} platformapp::${this.app.name}`)
                    },

                    session: this.$store.getters.getUser,
                },

                "@protokol/event.js": {
                    listen(e, fn) {
                        window.addEventListener(e, (ev) => {
                            fn(ev)
                        })
                    }
                },

                "@protokol/file.js": {
                    readAsDataUrl: (file, options) => {
                        return new Promise((resolve, reject) => {
                            var reader = new FileReader()
                            reader.readAsDataURL(file, options)
                            reader.onload = () => resolve(reader.result);
                            reader.onerror = error => reject(error);
                        })
                    }
                },

                "@protokol/eventbus.js": this.$eventBus,

                "@protokol/operator.js": {
                    async call(fname, ...args) {
                        if (!window.flutter_inappwebview) {
                            throw new Error("invalid platform");
                        }

                        return await window.flutter_inappwebview.callHandler(fname, ...args);
                    },

                    isOperatorApp() {
                        return window.flutter_inappwebview !== undefined
                    }
                },

                "@protokol/template/components": TemplateComponents,

                "@protokol/store.js": Vuex
            }
        }

    },

    destroyed() {
        this.$eventBus.close()
        document.querySelectorAll(`style#${CSS.escape(this.version)}`).forEach(style => style.remove())
    },

    mounted() {
        window.protokol = [];

        this.$eventBus.connect()

        let result = this.runJS(this.template.script, this.entrypoint)
        if (result && result.stack && result.message) {
            this.showErrorModal(`
                ${result.message}
                ----
                ${result.stack}
            `)
            return
        }

        try {
            this.parseStyles(this.template.style)

            result.components = {
                ...result.components || {},
                ...this.components,
            }

            const { render, staticRenderFns } = this.template

            let component = Vue.extend({
                name: `protokol-platform-app-template-${this.uuid}`,
                ...result,
                render: new Function(render),
                staticRenderFns: staticRenderFns.map(f => {
                    return new Function(f)
                })
            })

            var self = this
            let opts = {
                template: '<PowerApp v-on="inheritListeners" v-bind="inheritAttrs" />',
                store: this.$store,
                components: {
                    PowerApp: component
                },

                computed: {
                    inheritListeners() {
                        return self.$listeners
                    },

                    inheritAttrs() {
                        return self.$attrs
                    }
                }
            }

            const routes = this.loader().require('routes.js', { ignorePathError: true }) || []
            const i18nopts = this.loader().require('i18n.js', { ignorePathError: true }) || {}
            const storeopts = this.loader().require('store.js', { ignorePathError: true }) || {}

            if (!this.noRouter) {
                opts.router = new VueRouter({
                    // 4. Provide the history implementation to use. We are using the hash history for simplicity here.
                    routes, // short for `routes: routes`
                    base: this.$route.path
                })
            }

            opts.i18n = FactorI18N({
                ...i18nopts,
                locale: this.$store.getters.getLocale,
            })

            Object.keys(storeopts).forEach(store => {     
                this.$store.registerModule("", storeopts[store], { preserveState: false }) 
            })

            console.log("[Render] PowerAppV5 mounted", opts)
        
            const app = new Vue(opts)
            app.$mount(`.app-${this.app.uuid}`)
        } catch (err) {
            console.error(err)
        }
    },

    beforeDestroy() {
        // Get a reference to the last interval + 1
        const interval_id = window.setInterval(function () { }, Number.MAX_SAFE_INTEGER);

        // Clear any timeout/interval up to that id
        for (let i = 1; i < interval_id; i++) {
            window.clearInterval(i);
        }

        document.querySelectorAll(`style.appstyles-${this.app.uuid}`).forEach(styleEl => {
            styleEl.remove();
        });
    },

    updated() {
        console.log("[Render] PowerAppV5 rendered")
    },
}
</script>